import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../api/auth';
import { gameMusic } from '../lib/gameMusic';

const MUSIC_ENABLED_KEY = 'dios10_music_enabled';
const MUSIC_VOLUME_KEY = 'dios10_music_volume';
const DEFAULT_VOLUME = 0.2;

const MusicContext = createContext();

export const useMusic = () => {
  const context = useContext(MusicContext);
  if (!context) {
    throw new Error('useMusic must be used within a MusicProvider');
  }
  return context;
};

export const MusicProvider = ({ children }) => {
  const [enabled, setEnabledState] = useState(() => {
    try {
      const stored = localStorage.getItem(MUSIC_ENABLED_KEY);
      return stored === null ? true : stored === 'true';
    } catch {
      return true;
    }
  });
  const [volume, setVolumeState] = useState(() => {
    try {
      const stored = localStorage.getItem(MUSIC_VOLUME_KEY);
      if (stored !== null) {
        const v = parseFloat(stored);
        if (!isNaN(v) && v >= 0 && v <= 1) return v;
      }
    } catch {}
    return DEFAULT_VOLUME;
  });

  const setEnabled = useCallback((value) => {
    setEnabledState(value);
    try {
      localStorage.setItem(MUSIC_ENABLED_KEY, String(value));
    } catch (e) {
      console.warn('Could not save music preference:', e);
    }
    if (!value) gameMusic.pause();
  }, []);

  const setVolume = useCallback((value) => {
    const v = Math.max(0, Math.min(1, value));
    setVolumeState(v);
    try {
      localStorage.setItem(MUSIC_VOLUME_KEY, String(v));
    } catch (e) {
      console.warn('Could not save music volume:', e);
    }
    gameMusic.setVolume(v);
  }, []);

  // Avvia musica solo se: utente loggato, enabled, e dopo unlock (user gesture)
  const tryPlay = useCallback(() => {
    const user = authApi.getCurrentUser();
    if (!user || !enabled) {
      gameMusic.pause();
      return;
    }
    gameMusic.play(volume);
  }, [enabled, volume]);

  // Unlock: primo click/tap dell'utente (richiesto dai browser per autoplay)
  useEffect(() => {
    const unlock = () => {
      window.dispatchEvent(new CustomEvent('dios10:unlockAudio'));
    };
    document.addEventListener('click', unlock, { capture: true, once: true });
    document.addEventListener('touchstart', unlock, { capture: true, once: true });
    document.addEventListener('keydown', unlock, { capture: true, once: true });
    return () => {
      document.removeEventListener('click', unlock);
      document.removeEventListener('touchstart', unlock);
      document.removeEventListener('keydown', unlock);
    };
  }, []);

  // Al click utente: avvia musica (deve essere in risposta a user gesture)
  useEffect(() => {
    const onUnlock = () => {
      if (enabled) tryPlay();
    };
    window.addEventListener('dios10:unlockAudio', onUnlock);
    return () => window.removeEventListener('dios10:unlockAudio', onUnlock);
  }, [enabled, tryPlay]);

  // Al login: avvia musica se già unlockato
  useEffect(() => {
    const onLogin = () => {
      if (enabled) tryPlay();
    };
    window.addEventListener('dios10:userLoggedIn', onLogin);
    return () => window.removeEventListener('dios10:userLoggedIn', onLogin);
  }, [enabled, tryPlay]);

  // Quando enabled/volume cambiano e utente già loggato: aggiorna
  useEffect(() => {
    const user = authApi.getCurrentUser();
    if (user && enabled) {
      gameMusic.setVolume(volume);
      if (gameMusic.isPlaying()) {
        // già in play, solo aggiorna volume
      } else {
        tryPlay();
      }
    } else if (!enabled) {
      gameMusic.pause();
    }
  }, [enabled, volume, tryPlay]);

  // Logout: ferma
  useEffect(() => {
    const onLogout = () => gameMusic.pause();
    window.addEventListener('dios10:userLoggedOut', onLogout);
    return () => window.removeEventListener('dios10:userLoggedOut', onLogout);
  }, []);

  const value = {
    enabled,
    setEnabled,
    volume,
    setVolume,
    volumePercent: Math.round(volume * 100)
  };

  return (
    <MusicContext.Provider value={value}>
      {children}
    </MusicContext.Provider>
  );
};
